// Plural forms in messages (no JSON imports: the translate tool runs this under plain Node).

/**
 * Plural forms inside a message: `{name, plural, one {…} few {…} other {…}}`; `#` inside stands for the number
 * (formatted like any placeholder). The form is the language's own plural category for the number (Intl.PluralRules,
 * CLDR: Russian and Ukrainian one/few/many/other, Arabic zero/one/two/few/many/other, Hebrew one/two/other, English
 * one/other; Matt, 2026-10-16), else `other`. English and Hebrew texts are written with `one` and `other` (a Hebrew `two`
 * is optional: "2 ימים" is right too). Without a language: `one` for exactly 1, else `other`.
 */
export function resolvePlurals(template: string, params: Record<string, string | number>, lang?: string, fractionDigits?: number): string {
  let out = '';
  let i = 0;
  const head = /\{(\w+), plural,/g;
  for (let m = head.exec(template); m; m = head.exec(template)) {
    const name = m[1] ?? '';
    const forms: Record<string, string> = {};
    let j = m.index + m[0].length;
    // Each form: optional spaces, a word, spaces, then a balanced {…} block.
    for (;;) {
      const form = /^\s*(\w+)\s*\{/.exec(template.slice(j));
      if (!form) break;
      let depth = 1;
      let k = j + form[0].length;
      for (; k < template.length && depth > 0; k++) depth += template[k] === '{' ? 1 : template[k] === '}' ? -1 : 0;
      forms[form[1] ?? ''] = template.slice(j + form[0].length, k - 1);
      j = k;
    }
    const close = template.indexOf('}', j);
    if (close < 0 || forms.other === undefined) continue; // malformed: leave it as written
    const chosen = forms[pluralCategory(params[name], lang, fractionDigits)] ?? forms.other;
    out += template.slice(i, m.index) + chosen.replaceAll('#', `{${name}}`);
    i = close + 1;
    head.lastIndex = i;
  }
  return out + template.slice(i);
}

/** The plural category of a count in a language (`other` for anything that isn't a number). */
export function pluralCategory(count: string | number | undefined, lang?: string, fractionDigits?: number): string {
  const n = typeof count === 'number' ? count : Number(count);
  if (count === undefined || count === '' || !Number.isFinite(n)) return 'other';
  if (!lang) return n === 1 ? 'one' : 'other';
  // `fractionDigits`: the decimals the number is shown with ("1.00" is not "one" in English).
  return new Intl.PluralRules(lang, fractionDigits === undefined ? {} : { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).select(n);
}

/** Every plural category a language has (Intl.PluralRules), e.g. ru → one, few, many, other. */
export const pluralCategories = (lang: string): string[] => [...new Intl.PluralRules(lang).resolvedOptions().pluralCategories];

/**
 * The categories a translation into `lang` must give: every one a whole count from 0 to 200 can select, plus `other`
 * (ru → one, few, many, other; fr → one, other: its `many` is only for millions).
 */
export function requiredPluralCategories(lang: string): string[] {
  const rules = new Intl.PluralRules(lang);
  const used = new Set(['other', ...Array.from({ length: 201 }, (_, n) => rules.select(n))]);
  return pluralCategories(lang).filter((c) => used.has(c));
}

/** Whether a plural form of `lang` is used by more than one whole count (ru `one`: 1, 21, 31…; en `one`: only 1). */
export function formCoversSeveral(lang: string, form: string): boolean {
  const rules = new Intl.PluralRules(lang);
  return Array.from({ length: 201 }, (_, n) => rules.select(n)).filter((c) => c === form).length > 1;
}

/** The plural blocks of a message: each one's count name, its forms in order, and each form's text. */
export function pluralBlocks(template: string): { name: string; forms: string[]; texts: Record<string, string> }[] {
  const blocks: { name: string; forms: string[]; texts: Record<string, string> }[] = [];
  const head = /\{(\w+), plural,/g;
  for (let m = head.exec(template); m; m = head.exec(template)) {
    const forms: string[] = [];
    const texts: Record<string, string> = {};
    let j = m.index + m[0].length;
    for (;;) {
      const form = /^\s*(\w+)\s*\{/.exec(template.slice(j));
      if (!form) break;
      let depth = 1;
      let k = j + form[0].length;
      for (; k < template.length && depth > 0; k++) depth += template[k] === '{' ? 1 : template[k] === '}' ? -1 : 0;
      forms.push(form[1] ?? '');
      texts[form[1] ?? ''] = template.slice(j + form[0].length, k - 1);
      j = k;
    }
    blocks.push({ name: m[1] ?? '', forms, texts });
  }
  return blocks;
}

/** The plural blocks of a message, as `name:form,form` (for the translate tool: a translation must keep them). */
export function pluralShape(template: string): string {
  const shapes: string[] = [];
  const head = /\{(\w+), plural,/g;
  for (let m = head.exec(template); m; m = head.exec(template)) {
    const forms: string[] = [];
    let j = m.index + m[0].length;
    for (;;) {
      const form = /^\s*(\w+)\s*\{/.exec(template.slice(j));
      if (!form) break;
      let depth = 1;
      let k = j + form[0].length;
      for (; k < template.length && depth > 0; k++) depth += template[k] === '{' ? 1 : template[k] === '}' ? -1 : 0;
      forms.push(form[1] ?? '');
      j = k;
    }
    shapes.push(`${m[1]}:${forms.sort().join(',')}`);
  }
  return shapes.sort().join(';');
}
